import type { CoreApiSchemas } from '@hive/contracts';
import { session } from '../auth/session';

export type ScreenEvent = CoreApiSchemas['ScreenEvent'];
export type OrderEvent = CoreApiSchemas['OrderEvent'];
export type ScanEvent = CoreApiSchemas['ScanEvent'];

/** What the stream carries: screen changes, Nectar orders (for staff who run the queue) and QR scans. */
export type LiveEvent = { kind: 'screen'; event: ScreenEvent } | { kind: 'order'; event: OrderEvent } | { kind: 'scan'; event: ScanEvent };

type Listener = { onEvent(event: LiveEvent): void; onConnection(connected: boolean): void };

const baseUrl = import.meta.env.VITE_API_BASE_URL as string;
const KINDS = ['screen', 'order', 'scan'] as const;

// One connection per page, shared by every part of it that wants live updates.
const listeners = new Set<Listener>();
let disconnect: (() => void) | null = null;
let connected = false;

/** Screen events only. */
export function subscribeScreenEvents(onEvent: (event: ScreenEvent) => void, onConnection: (connected: boolean) => void): () => void {
  return subscribeLive((e) => e.kind === 'screen' && onEvent(e.event), onConnection);
}

/**
 * Every event on GET /v1/stream (server-sent events). The page holds one connection,
 * opened with the first subscriber and closed with the last; it reconnects with backoff.
 * Uses fetch rather than EventSource so the access token travels in the Authorization
 * header; works the same in a browser and in a Capacitor WebView.
 */
export function subscribeLive(onEvent: (event: LiveEvent) => void, onConnection: (connected: boolean) => void): () => void {
  const listener: Listener = { onEvent, onConnection };
  listeners.add(listener);
  if (connected) onConnection(true);
  if (!disconnect) {
    disconnect = connect(
      (e) => listeners.forEach((l) => l.onEvent(e)),
      (c) => {
        connected = c;
        listeners.forEach((l) => l.onConnection(c));
      },
    );
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && disconnect) {
      disconnect();
      disconnect = null;
      connected = false;
    }
  };
}

function connect(onEvent: (event: LiveEvent) => void, onConnection: (connected: boolean) => void): () => void {
  let stopped = false;
  let controller: AbortController | null = null;
  let backoff = 1000;

  async function run() {
    while (!stopped) {
      const token = session.token();
      if (!token) return;
      controller = new AbortController();
      try {
        const res = await fetch(`${baseUrl}/v1/stream`, {
          headers: { Authorization: `Bearer ${token}`, Accept: 'text/event-stream' },
          signal: controller.signal,
        });
        if (res.status === 401) {
          session.clear();
          return;
        }
        if (!res.ok || !res.body) throw new Error(`stream ${res.status}`);
        onConnection(true);
        backoff = 1000;
        await read(res.body, onEvent);
      } catch {
        /* network error or abort; fall through to reconnect */
      }
      onConnection(false);
      if (stopped) return;
      await new Promise((r) => setTimeout(r, backoff));
      backoff = Math.min(backoff * 2, 15000);
    }
  }

  void run();
  return () => {
    stopped = true;
    controller?.abort();
  };
}

async function read(body: ReadableStream<Uint8Array>, onEvent: (event: LiveEvent) => void) {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) return;
    buffer += decoder.decode(value, { stream: true });
    let sep: number;
    while ((sep = buffer.indexOf('\n\n')) >= 0) {
      const lines = buffer.slice(0, sep).split('\n');
      buffer = buffer.slice(sep + 2);
      const kind = KINDS.find((k) => lines.includes(`event: ${k}`));
      const data = lines.find((l) => l.startsWith('data: '));
      if (!kind || !data) continue;
      onEvent({ kind, event: JSON.parse(data.slice(6)) } as LiveEvent);
    }
  }
}
